import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import ReactApexChart from "react-apexcharts";
import axiosInstance from "../config/axiosInstance";
import { CRM, DASHBOARD } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";

const pad2 = (value) => String(value).padStart(2, "0");

const formatDateKey = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const buildDefaultRange = () => {
  const today = new Date();
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: formatDateKey(start),
    endDate: formatDateKey(end),
  };
};

const fetchOperationsDashboard = ({ startDate, endDate, funnelIds = [] }) => {
  const params = { startDate, endDate };
  if (funnelIds.length) {
    params.funnelIds = funnelIds;
  }

  return axiosInstance
    .get(DASHBOARD.GET_OPERATIONS, { params })
    .then((response) => response.data.data);
};

const fetchFunnels = () =>
  axiosInstance
    .get(CRM.FUNNELS)
    .then((response) => response.data.data ?? [])
    .then((list) =>
      Array.isArray(list) ? list.filter((funnel) => funnel?.isMetrified) : []
    );

export default function UserRoutineMetricsPage() {
  const { userPermission } = useApplication();
  const isPowerUser = String(userPermission || "").includes("POWER_USER");

  const [range, setRange] = useState(buildDefaultRange);
  const [selectedFunnelIds, setSelectedFunnelIds] = useState([]);
  const [funnelSearch, setFunnelSearch] = useState("");
  const [isFunnelAutocompleteOpen, setIsFunnelAutocompleteOpen] =
    useState(false);

  const {
    data: funnels = [],
    isLoading: isLoadingFunnels,
    isError: isFunnelsError,
    error: funnelsError,
  } = useQuery("crmFunnels", fetchFunnels, {
    enabled: isPowerUser,
  });

  useEffect(() => {
    if (!selectedFunnelIds.length && funnels.length) {
      setSelectedFunnelIds([String(funnels[0].id)]);
    }
  }, [funnels, selectedFunnelIds.length]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    [
      "routineMetricsDashboard",
      range.startDate,
      range.endDate,
      selectedFunnelIds.join(","),
    ],
    () =>
      fetchOperationsDashboard({
        startDate: range.startDate,
        endDate: range.endDate,
        funnelIds: selectedFunnelIds,
      }),
    {
      enabled: isPowerUser && Boolean(range.startDate && range.endDate),
    }
  );

  const taskCompletion = data?.taskCompletion ?? [];

  const userRoutineMetrics = useMemo(
    () =>
      taskCompletion
        .map((user) => {
          const totalTasks = Number(user?.totalTasks ?? 0);
          const completedTasks = Number(user?.completedTasks ?? 0);
          const completionRate = Number(user?.completionRate ?? 0);
          const pendingTasks = Math.max(totalTasks - completedTasks, 0);

          return {
            ...user,
            totalTasks,
            completedTasks,
            completionRate,
            pendingTasks,
            userKey: user?.userId
              ? `id:${String(user.userId)}`
              : `name:${String(user?.userName || "")}`,
          };
        })
        .sort((a, b) => {
          if (b.completedTasks !== a.completedTasks) {
            return b.completedTasks - a.completedTasks;
          }
          return b.completionRate - a.completionRate;
        }),
    [taskCompletion]
  );
  const [selectedUserKey, setSelectedUserKey] = useState("");

  const teamTotals = useMemo(
    () => ({
      users: userRoutineMetrics.length,
      assigned: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.totalTasks || 0),
        0
      ),
      completed: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.completedTasks || 0),
        0
      ),
      pending: userRoutineMetrics.reduce(
        (sum, user) => sum + Number(user.pendingTasks || 0),
        0
      ),
      averageRate: userRoutineMetrics.length
        ? Math.round(
            userRoutineMetrics.reduce(
              (sum, user) => sum + Number(user.completionRate || 0),
              0
            ) / userRoutineMetrics.length
          )
        : 0,
    }),
    [userRoutineMetrics]
  );

  useEffect(() => {
    if (!userRoutineMetrics.length) {
      setSelectedUserKey("");
      return;
    }

    const hasSelected = userRoutineMetrics.some(
      (user) => user.userKey === selectedUserKey
    );
    if (!selectedUserKey || !hasSelected) {
      setSelectedUserKey(userRoutineMetrics[0].userKey);
    }
  }, [userRoutineMetrics, selectedUserKey]);

  const selectedUser = useMemo(
    () => userRoutineMetrics.find((user) => user.userKey === selectedUserKey) || null,
    [userRoutineMetrics, selectedUserKey]
  );

  const periodDays = useMemo(() => {
    if (!range.startDate || !range.endDate) return 1;
    const start = new Date(`${range.startDate}T00:00:00`);
    const end = new Date(`${range.endDate}T00:00:00`);
    const diffMs = end.getTime() - start.getTime();
    if (!Number.isFinite(diffMs) || diffMs < 0) return 1;
    return Math.floor(diffMs / 86400000) + 1;
  }, [range.startDate, range.endDate]);

  const selectedUserDetails = useMemo(() => {
    if (!selectedUser) {
      return null;
    }

    const assignedShare = teamTotals.assigned
      ? (selectedUser.totalTasks / teamTotals.assigned) * 100
      : 0;
    const completedShare = teamTotals.completed
      ? (selectedUser.completedTasks / teamTotals.completed) * 100
      : 0;
    const pendingShare = teamTotals.pending
      ? (selectedUser.pendingTasks / teamTotals.pending) * 100
      : 0;
    const pendingRate = selectedUser.totalTasks
      ? (selectedUser.pendingTasks / selectedUser.totalTasks) * 100
      : 0;
    const throughputPerDay = selectedUser.completedTasks / periodDays;
    const backlogDaysEstimate =
      throughputPerDay > 0 ? selectedUser.pendingTasks / throughputPerDay : null;
    const ranking = userRoutineMetrics.findIndex(
      (user) => user.userKey === selectedUser.userKey
    );

    return {
      assignedShare,
      completedShare,
      pendingShare,
      pendingRate,
      throughputPerDay,
      backlogDaysEstimate,
      ranking: ranking >= 0 ? ranking + 1 : null,
      completionDeltaVsTeam: selectedUser.completionRate - teamTotals.averageRate,
    };
  }, [selectedUser, teamTotals, periodDays, userRoutineMetrics]);

  const teamCompletionRate = useMemo(() => {
    if (!teamTotals.assigned) {
      return 0;
    }
    return (teamTotals.completed / teamTotals.assigned) * 100;
  }, [teamTotals.assigned, teamTotals.completed]);

  const teamWorkloadSeries = useMemo(
    () => [
      {
        name: "Concluídas",
        data: userRoutineMetrics.map((user) => user.completedTasks),
      },
      {
        name: "Pendentes",
        data: userRoutineMetrics.map((user) => user.pendingTasks),
      },
    ],
    [userRoutineMetrics]
  );

  const teamWorkloadOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 6,
          barHeight: "58%",
        },
      },
      xaxis: {
        categories: userRoutineMetrics.map((user) => user.userName || "Usuário"),
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: { labels: { style: { fontSize: "11px" } } },
      colors: ["#059669", "#F59E0B"],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      grid: { borderColor: "#EEF2F7" },
      tooltip: {
        y: {
          formatter: (value) => `${Math.round(value)} tarefas`,
        },
      },
    }),
    [userRoutineMetrics]
  );

  const selectedUserDonutSeries = useMemo(() => {
    if (!selectedUser) return [0, 0];
    return [selectedUser.completedTasks, selectedUser.pendingTasks];
  }, [selectedUser]);

  const selectedUserDonutOptions = useMemo(
    () => ({
      chart: { type: "donut", toolbar: { show: false } },
      labels: ["Concluídas", "Pendentes"],
      colors: ["#10B981", "#F59E0B"],
      legend: { position: "bottom" },
      dataLabels: {
        enabled: true,
        formatter: (value) => `${value.toFixed(0)}%`,
      },
      stroke: { colors: ["#ffffff"] },
      tooltip: {
        y: {
          formatter: (value) => `${Math.round(value)} tarefas`,
        },
      },
    }),
    []
  );

  const toggleFunnel = (funnelId) => {
    setSelectedFunnelIds((prev) => {
      if (prev.includes(funnelId)) {
        return prev.filter((id) => id !== funnelId);
      }
      return [...prev, funnelId];
    });
  };

  const selectedFunnels = useMemo(
    () =>
      selectedFunnelIds
        .map((id) => funnels.find((funnel) => String(funnel.id) === id))
        .filter(Boolean),
    [selectedFunnelIds, funnels]
  );

  const filteredFunnels = useMemo(() => {
    const term = funnelSearch.trim().toLowerCase();
    const available = funnels.filter(
      (funnel) => !selectedFunnelIds.includes(String(funnel.id))
    );

    if (!term) {
      return available.slice(0, 8);
    }

    return available
      .filter((funnel) => String(funnel.name || "").toLowerCase().includes(term))
      .slice(0, 8);
  }, [funnelSearch, funnels, selectedFunnelIds]);

  const handleFunnelAutocompleteSelect = (funnelId) => {
    toggleFunnel(String(funnelId));
    setFunnelSearch("");
    setIsFunnelAutocompleteOpen(false);
  };

  if (!isPowerUser) {
    return (
      <div className="mx-auto max-w-4xl">
        <div className="rounded-3xl border border-rose-100 bg-rose-50 p-8 text-center">
          <h1 className="text-xl font-bold text-rose-800">
            Acesso restrito
          </h1>
          <p className="mt-2 text-sm text-rose-700">
            Esta seção é exclusiva para usuários com permissão POWER_USER.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 text-white shadow-lg">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-white/70">
              Administração
            </p>
            <h1 className="mt-2 text-2xl font-bold">
              Acompanhamento de rotinas e métricas por usuário
            </h1>
            <p className="mt-1 text-sm text-white/80">
              Acompanhe produtividade e execução das rotinas por responsável.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="rounded-2xl border border-gray-100 p-4 lg:col-span-1">
            <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500 block mb-2">
              Período
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="date"
                className="rounded-lg border-gray-300 text-sm"
                value={range.startDate}
                max={range.endDate}
                onChange={(event) =>
                  setRange((prev) => ({ ...prev, startDate: event.target.value }))
                }
              />
              <input
                type="date"
                className="rounded-lg border-gray-300 text-sm"
                value={range.endDate}
                min={range.startDate}
                onChange={(event) =>
                  setRange((prev) => ({ ...prev, endDate: event.target.value }))
                }
              />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 p-4 lg:col-span-2">
            <div className="flex items-center justify-between gap-2 mb-2">
              <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500">
                Funis metrificados
              </label>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-xs text-slate-600 hover:text-slate-900"
              >
                Atualizar
              </button>
            </div>
            {isLoadingFunnels ? (
              <p className="text-sm text-gray-500">Carregando funis...</p>
            ) : isFunnelsError ? (
              <p className="text-sm text-rose-600">
                Falha ao carregar funis: {funnelsError?.message || "erro"}
              </p>
            ) : funnels.length ? (
              <div className="space-y-3">
                <div
                  className="relative"
                  onFocus={() => setIsFunnelAutocompleteOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setIsFunnelAutocompleteOpen(false), 120);
                  }}
                >
                  <input
                    type="text"
                    value={funnelSearch}
                    onChange={(event) => {
                      setFunnelSearch(event.target.value);
                      setIsFunnelAutocompleteOpen(true);
                    }}
                    placeholder="Buscar funil para adicionar..."
                    className="w-full rounded-lg border-gray-300 text-sm"
                  />

                  {isFunnelAutocompleteOpen && filteredFunnels.length ? (
                    <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {filteredFunnels.map((funnel) => (
                        <button
                          key={String(funnel.id)}
                          type="button"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            handleFunnelAutocompleteSelect(String(funnel.id));
                          }}
                          className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-slate-50"
                        >
                          {funnel.name}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedFunnels.length ? (
                    selectedFunnels.map((funnel) => (
                      <span
                        key={String(funnel.id)}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                      >
                        {funnel.name}
                        <button
                          type="button"
                          onClick={() => toggleFunnel(String(funnel.id))}
                          className="rounded-full px-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                          aria-label={`Remover funil ${funnel.name}`}
                        >
                          x
                        </button>
                      </span>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500">Nenhum funil selecionado.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nenhum funil metrificado encontrado.</p>
            )}
          </div>

          <div className="rounded-2xl border border-gray-100 p-4 lg:col-span-1">
            <label className="text-[11px] uppercase tracking-[0.3em] text-gray-500 block mb-2">
              Usuário
            </label>
            <select
              className="w-full rounded-lg border-gray-300 text-sm"
              value={selectedUserKey}
              onChange={(event) => setSelectedUserKey(event.target.value)}
              disabled={isLoading || !userRoutineMetrics.length}
            >
              {!userRoutineMetrics.length ? (
                <option value="">Sem usuários no período</option>
              ) : null}
              {userRoutineMetrics.map((user) => (
                <option key={user.userKey} value={user.userKey}>
                  {user.userName}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Selecione um responsável para ver a análise completa.
            </p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <VisualMetricCard
          label="Usuários monitorados"
          value={teamTotals.users}
          note="Responsáveis com tarefas no período"
          tone="bg-cyan-600"
        />
        <VisualMetricCard
          label="Tarefas atribuídas"
          value={teamTotals.assigned}
          note="Carga total distribuída"
          tone="bg-indigo-600"
        />
        <VisualMetricCard
          label="Tarefas concluídas"
          value={teamTotals.completed}
          note={`${teamCompletionRate.toFixed(1)}% de execução do time`}
          tone="bg-emerald-600"
        />
        <VisualMetricCard
          label="Backlog atual"
          value={teamTotals.pending}
          note={`${teamTotals.averageRate}% de média de conclusão`}
          tone="bg-amber-500"
        />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="md:col-span-2 rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Visão comparativa do time</h2>
              <p className="text-xs text-gray-500">
                Distribuição de tarefas concluídas e pendentes por usuário.
              </p>
            </div>
          </div>

          {userRoutineMetrics.length ? (
            <div className="mt-4">
              <ReactApexChart
                options={teamWorkloadOptions}
                series={teamWorkloadSeries}
                type="bar"
                height={Math.max(300, userRoutineMetrics.length * 46)}
              />
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              Sem dados para montar o comparativo.
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-bold text-gray-900">Perfil do usuário selecionado</h2>
          <p className="text-xs text-gray-500">
            Composição entre concluídas e pendentes.
          </p>

          {selectedUser ? (
            <>
              <div className="mt-4">
                <ReactApexChart
                  options={selectedUserDonutOptions}
                  series={selectedUserDonutSeries}
                  type="donut"
                  height={250}
                />
              </div>
              <div className="mt-2 rounded-2xl border border-gray-100 bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-900">{selectedUser.userName}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Ranking {selectedUserDetails?.ranking || "-"} de {teamTotals.users} no período.
                </p>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-sm text-gray-500">
              Selecione um usuário para visualizar o gráfico.
            </div>
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Detalhamento do usuário</h2>
          <p className="text-xs text-gray-500">
            Visão completa de execução, carga e eficiência no período selecionado.
          </p>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Carregando métricas...</div>
        ) : isError ? (
          <div className="p-8 text-center text-sm text-rose-600">
            Falha ao carregar métricas: {error?.message || "erro desconhecido"}
          </div>
        ) : selectedUser && selectedUserDetails ? (
          <div className="p-5 space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Usuário selecionado</p>
              <p className="mt-1 text-xl font-semibold text-slate-900">{selectedUser.userName}</p>
              <p className="mt-1 text-xs text-slate-600">
                Posição no período: {selectedUserDetails.ranking || "-"} de {teamTotals.users}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              <MetricCard
                label="Tarefas atribuídas"
                value={selectedUser.totalTasks}
                note={`${selectedUserDetails.assignedShare.toFixed(1)}% da carga do time`}
              />
              <MetricCard
                label="Tarefas concluídas"
                value={selectedUser.completedTasks}
                note={`${selectedUserDetails.completedShare.toFixed(1)}% das conclusões do time`}
              />
              <MetricCard
                label="Tarefas pendentes"
                value={selectedUser.pendingTasks}
                note={`${selectedUserDetails.pendingShare.toFixed(1)}% do backlog do time`}
              />
              <MetricCard
                label="Taxa de conclusão"
                value={`${selectedUser.completionRate}%`}
                note={`${
                  selectedUserDetails.completionDeltaVsTeam >= 0 ? "+" : ""
                }${selectedUserDetails.completionDeltaVsTeam.toFixed(1)} p.p. vs média do time`}
              />
              <MetricCard
                label="Ritmo diário"
                value={`${selectedUserDetails.throughputPerDay.toFixed(2)}/dia`}
                note={`Período analisado: ${periodDays} dia(s)`}
              />
              <MetricCard
                label="Estimativa de zerar pendências"
                value={
                  selectedUserDetails.backlogDaysEstimate == null
                    ? "Sem base"
                    : `${Math.ceil(selectedUserDetails.backlogDaysEstimate)} dia(s)`
                }
                note="Baseado no ritmo de conclusão do período"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Eficiência operacional</p>
                <p className="text-xs text-gray-500 mt-1">
                  Relação entre tarefas concluídas e pendentes do usuário.
                </p>
                <div className="mt-4 h-3 w-full rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${selectedUser.completionRate}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Concluídas: {selectedUser.completedTasks}</span>
                  <span>Pendentes: {selectedUser.pendingTasks}</span>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-sm font-semibold text-gray-900">Risco de backlog</p>
                <p className="text-xs text-gray-500 mt-1">
                  Percentual de tarefas pendentes dentro da carteira do usuário.
                </p>
                <div className="mt-4 h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-500"
                    style={{ width: `${selectedUserDetails.pendingRate.toFixed(1)}%` }}
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Taxa de pendência</span>
                  <span>{selectedUserDetails.pendingRate.toFixed(1)}%</span>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">Contexto da equipe</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <p className="text-xs text-gray-500">Total atribuído no time</p>
                  <p className="font-semibold text-gray-900">{teamTotals.assigned}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <p className="text-xs text-gray-500">Total concluído no time</p>
                  <p className="font-semibold text-gray-900">{teamTotals.completed}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white px-3 py-2">
                  <p className="text-xs text-gray-500">Média de conclusão do time</p>
                  <p className="font-semibold text-gray-900">{teamTotals.averageRate}%</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-gray-500">
            Nenhum dado de rotina encontrado para o período selecionado.
          </div>
        )}
      </section>
    </div>
  );
}

function MetricCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4">
      <p className="text-[11px] uppercase tracking-[0.3em] text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{note}</p>
    </div>
  );
}

function VisualMetricCard({ label, value, note, tone }) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-3xl border border-white/30 p-5 shadow-md",
        "text-white",
        tone,
      ].join(" ")}
    >
      <p className="text-[11px] uppercase tracking-[0.3em] text-white/80">{label}</p>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
      <p className="mt-2 text-xs text-white/80">{note}</p>
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-white/20 blur-2xl" />
    </div>
  );
}
